import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Company, CompanyType } from '../types';
import { Card, CardContent, Button, Badge, EmptyState, Dialog} from '../components/ui';
import Layout from '../components/Layout';

const COMPANY_TYPES: { value: CompanyType; label: string }[] = [
  { value: 'brokerage', label: 'Brokerage' },
  { value: 'investment_firm', label: 'Investment Firm' },
  { value: 'developer', label: 'Developer' },
  { value: 'tenant_company', label: 'Tenant Company' },
  { value: 'lender', label: 'Lender' },
  { value: 'law_firm', label: 'Law Firm' },
  { value: 'management_company', label: 'Management Company' },
  { value: 'construction', label: 'Construction' },
  { value: 'appraisal_firm', label: 'Appraisal Firm' },
  { value: 'title_company', label: 'Title Company' },
  { value: 'other', label: 'Other' },
];

const typeBadgeVariant = (type: CompanyType) => {
  const map: Record<string, 'info' | 'success' | 'warning' | 'primary' | 'default'> = {
    brokerage: 'primary', investment_firm: 'success', developer: 'warning',
    lender: 'info', law_firm: 'default', tenant_company: 'info',
  };
  return map[type] || 'default';
};

export default function Companies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', company_type: 'other' as CompanyType, industry: '',
    website: '', phone: '', email: '', address: '', city: '', state: '', zip: '', notes: '',
  });

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (typeFilter) params.company_type = typeFilter;
      const res = await api.listCompanies(params);
      setCompanies(res.companies || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCompanies();
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.createCompany(form);
      setShowModal(false);
      setForm({ name: '', company_type: 'other', industry: '', website: '', phone: '', email: '', address: '', city: '', state: '', zip: '', notes: '' });
      fetchCompanies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create company');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div>
        <div className="pb-4">
          {/* Wraps below ~250px, and the title block may shrink - the OO4/OO5 header pattern.
              Without it the action button was pushed past the viewport on the narrow pane. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="min-w-0">
              <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Companies</h1>
              <p className="text-gray-500 mt-1">{total} companies in your CRM</p>
            </div>
            <Button onClick={() => setShowModal(true)}>+ Add Company</Button>
          </div>
        </div>
      </div>

      <div className="pt-4">
        <div className="flex flex-wrap gap-3 mb-6">
          {/* ⭐ `basis` sets the WRAP POINT; `min-w-0` lets it shrink BELOW that when the viewport
              itself is narrower. `min-w-[280px]` did the first job and refused the second, so at
              250px - the narrowest Joseph's pane runs - this form was 280px wide and pushed the
              whole PAGE 46px sideways. Measured: the form's right edge was 296 in a 250 viewport.
              Same fix as the OO4 header row in DataHub. */}
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 basis-[280px] min-w-0">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies..."
              className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <Button type="submit" variant="outline" size="sm">Search</Button>
          </form>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Types</option>
            {COMPANY_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>
        )}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
                <p className="text-gray-500 mt-2">Loading companies...</p>
              </div>
            ) : companies.length === 0 ? (
              <EmptyState
                title="No companies found"
                description="Add your first company to organize your contacts."
                action={{ label: 'Add Company', onClick: () => setShowModal(true) }}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Type</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Industry</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Location</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Phone</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Email</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {companies.map((c) => (
                      <tr
                        key={c.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/crm/companies/${c.id}`)}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                        <td className="px-4 py-3">
                          <Badge variant={typeBadgeVariant(c.company_type)}>
                            {c.company_type?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{c.industry || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state || '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{c.phone || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{c.email || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Company Modal */}
      {/* OO5d: through the Dialog primitive. */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Add Company"
        size="lg"
        onSubmit={handleCreate}
        footer={<>
          <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Create Company'}</Button>
        </>}
      >
        <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.company_type}
                    onChange={(e) => setForm(f => ({ ...f, company_type: e.target.value as CompanyType }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <input
                    value={form.industry}
                    onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input value={form.state} onChange={(e) => setForm(f => ({ ...f, state: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                  <input value={form.zip} onChange={(e) => setForm(f => ({ ...f, zip: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
        </div>
      </Dialog>
    </Layout>
  );
}
